import {
  Component,
  DoCheck,
  ViewEncapsulation,
  forwardRef,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationModalComponent } from '../../notification-modal.component';
import { WashHistoryPanelComponent } from '../wash-history-panel/wash-history-panel.component';
import { ImagePreviewTriggerDirective } from './image-preview-trigger.directive';
import { PrepaidRedemptionComponent } from '../prepaid-redemption/prepaid-redemption.component';
import { OrderRequestPackagesBlockComponent } from '../order-request-packages-block/order-request-packages-block.component';
import { OrderRequestFamilySaleBlockComponent } from '../order-request-family-sale-block/order-request-family-sale-block.component';
import { OrderRequestPrepaidCardsComponent } from '../order-request-prepaid-cards/order-request-prepaid-cards.component';
import { SelectSearchComponent } from '../../../../shared/components/select-search/select-search.component';
import { SelectFilterPipe } from '../../../../shared/pipes/select-filter.pipe';
import { OrderRequestDiscountItem } from '../../models/notification-order-request.models';
import {
  formatOrderRequestDiscountRenewalTooltip,
  shouldShowOrderRequestDiscountRenewalInfo,
} from '../../utils/notification-order-request-discount.util';

/**
 * Order-request checkout UI (create-order-request, LPR, invalid-plate flows).
 * Logic stays on {@link NotificationModalComponent}; this component only holds the template.
 */
@Component({
  selector: 'app-notification-order-request-layout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatTooltipModule,
    WashHistoryPanelComponent,
    PrepaidRedemptionComponent,
    ImagePreviewTriggerDirective,
    SelectSearchComponent,
    SelectFilterPipe,
    OrderRequestPackagesBlockComponent,
    OrderRequestPrepaidCardsComponent,
    OrderRequestFamilySaleBlockComponent,
  ],
  templateUrl: './order-request-layout.component.html',
  // Full modal SCSS (global) — required so .radio-card, .form-group, etc. apply inside this template.
  styleUrls: ['../../notification-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class NotificationOrderRequestLayoutComponent implements DoCheck {
  readonly host = inject(
    forwardRef(() => NotificationModalComponent),
    { host: true }
  );
  private readonly cdr = inject(ChangeDetectorRef);

  /** Monthly Membership + advance_custom / advance_recurring only. */
  showDiscountRenewalInfo(discount: OrderRequestDiscountItem): boolean {
    return shouldShowOrderRequestDiscountRenewalInfo(discount);
  }

  /** Configured advance renewal details for the discount info tooltip. */
  discountRenewalTooltip(discount: OrderRequestDiscountItem): string {
    return formatOrderRequestDiscountRenewalTooltip(discount);
  }

  private lastHistoryLoading: boolean | null = null;
  private lastPrepaidStatusLoading: boolean | null = null;
  private lastPrepaidBooksLoading: boolean | null = null;
  private lastHistoryBanner: unknown = null;
  private lastPrepaidStatus: unknown = null;
  private lastPrepaidBooksLen = -1;

  /**
   * Host fields are read via inject() (no @Input). After Angular upgrades / live SSE,
   * parent property updates can leave this view on the first loading snapshot — sync here.
   */
  ngDoCheck(): void {
    const historyLoading = this.host.isLoadingOrderRequestHistoryPage;
    const prepaidStatusLoading = this.host.isLoadingPrepaidPurchaseStatus;
    const prepaidBooksLoading = this.host.isLoadingOrderRequestPrepaid;
    const banner = this.host.orderRequestTodayBanner;
    const prepaidStatus = this.host.prepaidPurchaseStatus;
    const booksLen = this.host.orderRequestPrepaidBooks?.length ?? 0;

    if (
      historyLoading !== this.lastHistoryLoading ||
      prepaidStatusLoading !== this.lastPrepaidStatusLoading ||
      prepaidBooksLoading !== this.lastPrepaidBooksLoading ||
      banner !== this.lastHistoryBanner ||
      prepaidStatus !== this.lastPrepaidStatus ||
      booksLen !== this.lastPrepaidBooksLen
    ) {
      this.lastHistoryLoading = historyLoading;
      this.lastPrepaidStatusLoading = prepaidStatusLoading;
      this.lastPrepaidBooksLoading = prepaidBooksLoading;
      this.lastHistoryBanner = banner;
      this.lastPrepaidStatus = prepaidStatus;
      this.lastPrepaidBooksLen = booksLen;
      this.cdr.markForCheck();
    }
  }
}
